/**
 * approvalAccessInvalidation — the post-decision re-read bus for the
 * Canonical_Approval_Reader
 * (approval-status-ssot-unification, Task 6 / Requirement 5).
 *
 * A successful Director_Decision_Action commits a new value to the Approval_SSOT
 * (`ApplicationResponse.submissionStatus`) via `reviewApplicationResponse`. Every
 * approval-gating reader in the tree derives from that SSOT through the
 * Canonical_Approval_Reader (`useApprovalAccess` / `useApprovalAccessList`), but a
 * decision surface and the readers it affects are independent hook instances —
 * without a signal, the readers keep serving their last SSOT read until they
 * happen to re-mount, so the decision is invisible until a manual reload.
 *
 * This module is that signal: a process-wide invalidation counter the decision
 * surfaces bump on success, and which every Canonical_Approval_Reader subscribes
 * to. When it bumps, each reader re-reads the Approval_SSOT (Req 5.5 — reflected
 * within 5s, no reload). Because the readers set their loading state and clear
 * any prior value at the start of the triggered re-read, they present loading
 * (never a stale approval-unlocked state) while it is in flight (Req 5.6), and a
 * failed re-read resolves to not-approval-unlocked plus an error (Req 5.7) — the
 * existing read-failure discipline in the readers.
 *
 * A plain external store (subscribe + snapshot) so it composes with React 18's
 * `useSyncExternalStore` and needs no provider/context — mirroring the module
 * cache already in `useApprovalAccess.ts`.
 */

import { useSyncExternalStore } from 'react';

let version = 0;
const listeners = new Set<() => void>();

/**
 * Bump the invalidation counter and notify every subscribed
 * Canonical_Approval_Reader to re-read the Approval_SSOT. Call this AFTER a
 * Director_Decision_Action commits successfully (never on failure — a failed
 * decision leaves the SSOT unchanged, so there is nothing new to read).
 */
export function invalidateApprovalAccess(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** Subscribe to invalidations. Returns an unsubscribe fn (useSyncExternalStore contract). */
export function subscribeApprovalAccess(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current invalidation version — the external-store snapshot. */
export function getApprovalAccessVersion(): number {
  return version;
}

/**
 * Subscribe a Canonical_Approval_Reader to the invalidation bus. The returned
 * number changes on every `invalidateApprovalAccess()` call; feed it into the
 * reader's read-effect deps so a decision triggers a fresh Approval_SSOT read.
 */
export function useApprovalAccessInvalidation(): number {
  return useSyncExternalStore(
    subscribeApprovalAccess,
    getApprovalAccessVersion,
    getApprovalAccessVersion,
  );
}

/** Test-only: reset the module-scope bus between cases. */
export function __resetApprovalAccessInvalidation(): void {
  version = 0;
  listeners.clear();
}
